<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
            <Button variant="danger" @click="retry">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <div v-for="i in 3" :key="'skel-section-' + i" class="tw:mb-8">
                <div class="tw:h-5 tw:w-48 tw:bg-gray-200 tw:rounded tw:mb-1"></div>
                <div class="tw:h-4 tw:w-72 tw:bg-gray-100 tw:rounded tw:mb-4"></div>
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <div class="tw:flex tw:items-center tw:justify-center tw:h-64">
                        <svg class="tw:animate-spin tw:h-8 tw:w-8 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                            <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                    </div>
                </div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="hasData">
            <!-- Revenue + Attendance chart (both modes) -->
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.revenue_attendance_title') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.revenue_attendance_desc') }}</p>
                <Chart
                    type="LineChart"
                    :data="revenueAttendanceChartData"
                    :options="revenueAttendanceOptions"
                    :height="320"
                />
            </div>

            <!-- 2-column grid: accumulated + position -->
            <div class="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-8">
                <!-- Accumulated bar chart -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.accumulated_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.accumulated_desc') }}</p>
                    <Chart
                        type="ColumnChart"
                        :data="accumulatedChartData"
                        :options="accumulatedChartOptions"
                        :height="220"
                    />
                </div>

                <!-- Position chart (single: ComboChart with revenue bars; multi: LineChart with position lines) -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <template v-if="!isMultiSeason">
                        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.position_revenue_title') }}</h3>
                        <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.position_revenue_desc') }}</p>
                        <Chart
                            type="ComboChart"
                            :data="posicionChartData"
                            :options="positionRevenueOptions"
                            :height="220"
                        />
                    </template>
                    <template v-else>
                        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.position_title') }}</h3>
                        <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.position_desc') }}</p>
                        <Chart
                            type="LineChart"
                            :data="posicionChartData"
                            :options="multiPositionOptions"
                            :height="220"
                        />
                    </template>
                </div>
            </div>

            <!-- Comparison cards: single-season only -->
            <div v-if="!isMultiSeason && kpis" class="tw:grid tw:grid-cols-2 tw:gap-4 tw:max-w-2xl tw:mx-auto tw:mb-6">
                <div class="tw:flex tw:flex-col tw:gap-2 tw:text-center tw:p-4 tw:bg-gray-50 tw:rounded-lg">
                    <span class="tw:text-xs tw:text-gray-500 tw:uppercase tw:tracking-wider tw:font-medium">{{ $t('back.dashboard.evolution.kpis.revenue_change') }}</span>
                    <span class="tw:text-2xl tw:font-bold" :class="revenueChange >= 0 ? 'tw:text-green-600' : 'tw:text-red-600'">
                        <span>{{ revenueChange >= 0 ? '↑' : '↓' }}</span> {{ formatChangePct(revenueChange) }}
                    </span>
                </div>
                <div class="tw:flex tw:flex-col tw:gap-2 tw:text-center tw:p-4 tw:bg-gray-50 tw:rounded-lg">
                    <span class="tw:text-xs tw:text-gray-500 tw:uppercase tw:tracking-wider tw:font-medium">{{ $t('back.dashboard.evolution.kpis.attendance_change') }}</span>
                    <span class="tw:text-2xl tw:font-bold" :class="attendanceChange >= 0 ? 'tw:text-green-600' : 'tw:text-red-600'">
                        <span>{{ attendanceChange >= 0 ? '↑' : '↓' }}</span> {{ formatChangePct(attendanceChange) }}
                    </span>
                </div>
            </div>

            <!-- Racha badges: single-season only -->
            <div v-if="!isMultiSeason" class="tw:mb-8">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.racha_title') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.racha_desc') }}</p>
                <div v-if="rachaBadges.length" class="tw:flex tw:gap-2">
                    <Badge
                        v-for="(badge, bi) in rachaBadges"
                        :key="bi"
                        :variant="badge.variant"
                        :label="badge.label"
                    />
                </div>
                <span v-else class="tw:text-sm tw:text-gray-400">{{ $t('back.dashboard.evolution.racha_empty') }}</span>
            </div>

            <!-- ClubEvolutionTable: per-jornada per-club breakdown (single-season only) -->
            <div v-if="!isMultiSeason && perClubData.jornadas.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.club_table_title') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.club_table_desc') }}</p>

                <!-- Jornada selector tabs -->
                <div class="tw:flex tw:flex-wrap tw:gap-2 tw:mb-4" role="tablist" aria-label="Selector de jornada">
                    <button
                        v-for="(j, i) in perClubData.jornadas"
                        :key="j.jornada"
                        role="tab"
                        :aria-selected="i === clubTableIndex"
                        class="tw:px-3 tw:py-1.5 tw:text-sm tw:font-semibold tw:rounded-lg tw:transition-colors tw:duration-150"
                        :class="i === clubTableIndex
                            ? 'tw:bg-blue-600 tw:text-white tw:shadow-sm'
                            : 'tw:bg-gray-100 tw:text-gray-500 hover:tw:bg-gray-200'"
                        @click="clubTableIndex = i"
                    >
                        {{ j.jornada }}
                    </button>
                </div>

                <!-- Club table -->
                <div class="tw:overflow-x-auto">
                    <table class="tw:w-full tw:text-sm">
                        <thead>
                            <tr class="tw:border-b tw:border-gray-200">
                                <th class="tw:text-left tw:py-2 tw:pr-4 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Club</th>
                                <th class="tw:text-right tw:py-2 tw:px-3 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Ingresos</th>
                                <th class="tw:text-right tw:py-2 tw:px-3 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Asistencia</th>
                                <th class="tw:text-center tw:py-2 tw:px-3 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Pos.</th>
                                <th class="tw:text-center tw:py-2 tw:px-3 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Pts</th>
                                <th class="tw:text-left tw:py-2 tw:pl-3 tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider">Racha</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="club in perClubData.jornadas[clubTableIndex]?.clubs || []"
                                :key="club.clubName"
                                class="tw:border-b tw:border-gray-100 hover:tw:bg-gray-50 tw:transition-colors"
                            >
                                <td class="tw:py-3 tw:pr-4 tw:font-semibold tw:text-gray-900 tw:whitespace-nowrap">{{ club.clubName }}</td>
                                <td class="tw:py-3 tw:px-3 tw:text-right tw:font-mono tw:text-sm">{{ formatCurrency(club.revenue) }}</td>
                                <td class="tw:py-3 tw:px-3 tw:text-right tw:font-mono tw:text-sm">{{ formatNumber(club.attendance) }}</td>
                                <td class="tw:py-3 tw:px-3 tw:text-center tw:font-mono tw:font-semibold">
                                    {{ club.posicion !== null ? club.posicion + 'º' : '—' }}
                                </td>
                                <td class="tw:py-3 tw:px-3 tw:text-center tw:font-mono tw:font-semibold">{{ club.puntos }}</td>
                                <td class="tw:py-3 tw:pl-3">
                                    <span v-if="club.racha" class="tw:inline-flex tw:gap-0.5 tw:items-center">
                                        <span
                                            v-for="(r, ri) in club.racha.split('')"
                                            :key="ri"
                                            class="tw:w-5 tw:h-5 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-xs tw:font-bold tw:text-white"
                                            :class="RACHA_MAP[r.toLowerCase()]?.bgClass || 'tw:bg-red-500'"
                                            :title="RACHA_MAP[r.toLowerCase()]?.label || r"
                                        >{{ RACHA_MAP[r.toLowerCase()]?.label || r.toUpperCase() }}</span>
                                    </span>
                                    <span v-else class="tw:text-gray-400 tw:text-xs">—</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty state for table -->
                <p v-if="!perClubData.jornadas[clubTableIndex]?.clubs?.length" class="tw:py-4 tw:text-center tw:text-sm tw:text-gray-400">
                    No hay datos de clubes para esta jornada.
                </p>
            </div>

            <!-- Bracket analysis: position vs revenue/attendance correlation (single-season only) -->
            <div v-if="!isMultiSeason && bracketAnalysis" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-8">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.evolution.bracket_title') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.evolution.bracket_desc') }}</p>

                <div class="tw:grid tw:grid-cols-3 tw:gap-4">
                    <div
                        v-for="bracket in bracketAnalysis"
                        :key="bracket.label"
                        class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-gray-50"
                    >
                        <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-2">{{ bracket.label }} ({{ bracket.count }})</h4>
                        <div class="tw:space-y-1 tw:text-sm">
                            <p class="tw:flex tw:justify-between">
                                <span class="tw:text-gray-500">Ingresos medios</span>
                                <span class="tw:font-semibold">{{ formatCurrency(bracket.avgRevenue) }}</span>
                            </p>
                            <p class="tw:flex tw:justify-between">
                                <span class="tw:text-gray-500">Asistencia media</span>
                                <span class="tw:font-semibold">{{ formatNumber(bracket.avgAttendance) }}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Breakdown table: multi-season only -->
            <div v-if="isMultiSeason && seasonBreakdown?.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white">
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.evolution.breakdown_title') }}</h4>
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                            <th class="tw:pb-2">{{ $t('back.dashboard.evolution.col_season') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.evolution.col_revenue') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.evolution.col_attendance') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.evolution.col_position') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.evolution.col_points') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.evolution.col_racha') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in seasonBreakdown" :key="row.season" class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-2 tw:font-medium">{{ row.season }}</td>
                            <td class="tw:py-2 tw:text-right">{{ formatCurrency(row.revenueTotal) }}</td>
                            <td class="tw:py-2 tw:text-right">{{ formatNumber(row.attendanceAvg) }}</td>
                            <td class="tw:py-2 tw:text-right">{{ row.finalPosition !== null ? row.finalPosition : '—' }}</td>
                            <td class="tw:py-2 tw:text-right">{{ formatNumber(row.points) }}</td>
                            <td class="tw:py-2 tw:text-right">
                                <span v-if="row.racha" class="tw:inline-flex tw:gap-1">
                                    <Badge
                                        v-for="(char, ci) in lastRachaChars(row.racha)"
                                        :key="ci"
                                        :variant="rachaBadgeVariant(char)"
                                        :label="rachaBadgeLabel(char)"
                                    />
                                </span>
                                <span v-else class="tw:text-gray-400 tw:text-xs">{{ $t('back.dashboard.evolution.racha_empty') }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else-if="!loading && !error" class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.evolution.empty') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { computed, toRef, ref } from 'vue';
    import { useEvolution } from '@/composables/admin/dashboard/useEvolution';
    import { formatCurrency, formatNumber } from '@/utils/format';
    import { useRacha } from '@/composables/common/useRacha';
    import { RACHA_MAP } from '@/composables/common/rachaMap';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import Badge from '@/modules/admin/ui/Badge.vue';
    import Button from '@/modules/admin/ui/Button.vue';

    const props = defineProps({
        seasonIds: {
            type: Array,
            required: true,
        },
        seasons: {
            type: Array,
            required: true,
        },
    });



    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        evolutionData,
        loading,
        error,
        kpis,
        chartData,
        isMultiSeason,
        seasonBreakdown,
        refresh,
        accumulatedChartData,
        accumulatedChartOptions,
        revenueAttendanceOptions,
        positionRevenueOptions,
        multiPositionOptions,
        revenueChange,
        attendanceChange,
        perClubData,
        bracketAnalysis,
        rachaBadges,
    } = useEvolution(seasonIdsRef, seasonsRef);

    const { lastChars: lastRachaChars, badgeVariant: rachaBadgeVariant, badgeLabel: rachaBadgeLabel } = useRacha();

    /** Índice de jornada seleccionada para la tabla de evolución por club */
    const clubTableIndex = ref(0);

    /** Indica si hay datos para mostrar */
    const hasData = computed(() => {
        return seasonBreakdown.value?.length > 0 || chartData.value.size > 0;
    });

    // ==================== Chart Data ====================

    /** Revenue + Attendance LineChart data */
    const revenueAttendanceChartData = computed(() => {
        return chartData.value.get('revenue_attendance') || [];
    });

    /** Position chart data */
    const posicionChartData = computed(() => {
        return chartData.value.get('position_points') || [];
    });

    // ==================== Helpers ====================

    /** Formatea un cambio porcentual con signo */
    function formatChangePct(value) {
        if (value === null || typeof value !== 'number') return '—';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(1)}%`;
    }

    /** Retry handler */
    function retry() {
        refresh();
    }
</script>
